import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import CONFIG from "config/config";
import { parsePlaceDetailsToAddressFields } from "components/place-autocomplete/parseAddressComponents";

/**
 * Google Places autocomplete for the checkout billing address.
 *
 * Reuses what the app already has: the Maps key the maps/segments screens load
 * (`CONFIG.GOOGLE_API_KEY`) and `parsePlaceDetailsToAddressFields`, which
 * already maps place details to address1/address2/city/state/zip. Only the
 * country is read here, since that helper doesn't return it and the billing
 * form needs it.
 *
 * The library list matches the maps screens on purpose — `useJsApiLoader`
 * complains when the same page loads the script twice with different options.
 *
 * Degrades quietly: no key, a load failure or a Places error simply yields no
 * suggestions, and the caller keeps a plain typeable input.
 */
const LIBRARIES: ("geometry" | "places" | "drawing")[] = ["geometry", "places", "drawing"];

const DEBOUNCE_MS = 250;

export type AddressSuggestion = { placeId: string; description: string };

export type ResolvedAddress = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
};

export const useAddressAutocomplete = (countryCode?: string) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: CONFIG.GOOGLE_API_KEY,
    libraries: LIBRARIES,
    language: "en",
    version: "3.64",
  });

  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const serviceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesRef = useRef<google.maps.places.PlacesService | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ready = isLoaded && !!CONFIG.GOOGLE_API_KEY && !!window.google?.maps?.places;

  useEffect(() => {
    if (!ready) return;
    if (!serviceRef.current) serviceRef.current = new window.google.maps.places.AutocompleteService();
    // PlacesService needs an element for attributions; it is never displayed.
    if (!placesRef.current)
      placesRef.current = new window.google.maps.places.PlacesService(document.createElement("div"));
  }, [ready]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const search = useCallback(
    (input: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (!ready || input.trim().length < 3) {
        setSuggestions([]);
        return;
      }
      timerRef.current = setTimeout(() => {
        serviceRef.current?.getPlacePredictions(
          {
            input,
            types: ["address"],
            ...(countryCode ? { componentRestrictions: { country: countryCode.toLowerCase() } } : {}),
          },
          (predictions, status) => {
            if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
              setSuggestions([]);
              return;
            }
            setSuggestions(predictions.map((p) => ({ placeId: p.place_id, description: p.description })));
          }
        );
      }, DEBOUNCE_MS);
    },
    [ready, countryCode]
  );

  const resolve = useCallback(
    (placeId: string): Promise<ResolvedAddress | null> =>
      new Promise((res) => {
        if (!placesRef.current) {
          res(null);
          return;
        }
        placesRef.current.getDetails({ placeId, fields: ["address_components"] }, (place, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
            res(null);
            return;
          }
          const parsed = parsePlaceDetailsToAddressFields(place);
          const country = place.address_components?.find((c) => c.types?.includes("country"))?.short_name ?? "";
          res({
            addressLine1: parsed.address1,
            addressLine2: parsed.address2,
            city: parsed.city,
            state: parsed.state,
            zipCode: parsed.zip,
            country,
          });
        });
      }),
    []
  );

  const clear = useCallback(() => setSuggestions([]), []);

  return useMemo(() => ({ ready, suggestions, search, resolve, clear }), [ready, suggestions, search, resolve, clear]);
};
